import { DeleteOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core';
import { Button, Popconfirm, Space, Table } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { JobStatus } from '../components';

const useStyles = makeStyles(theme => ({
    table: {
        width: '100%',
        height: '100%',
        marginRight: theme.spacing(1)
    },
}))

/**
 * Jobs Table view
 */
const JobsTableView = ({ jobs, selectJob, loading, scheduleJob, stopJob, deleteJob, count, updateLimit, updatePage, page, limit, retryJob, setOrdering, onSelectTool, usersFilter, allTools, setUserName, setToolName, setFilterBy, setTypeBy, setToolId }) => {
    const classes = useStyles()
    const [typeFilters, setTypeFilters] = useState([])
    const [statusFilters, setStatusFilters] = useState([])

    useEffect(() => {
        // extract the unique creator names from jobs 
        if (jobs) {

    
            const types = [...new Set(jobs.map(j => j.type))]
            setTypeFilters(types.map(n => ({ text: n, value: n })))

            const status = [...new Set(jobs.map(j => j.status))]
            setStatusFilters(status.map(n => ({ text: n, value: n })))
        }
    }, [jobs, allTools, usersFilter])


    // jobs table schema
    const jobsViewSchema = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => a.id - b.id,
            render: (text, record) => <Button type="link" onClick={() => selectJob(record)} href={"job?id=" + record.id} size="small">
            <span className={classes.nameCell}>{record.id}</span> </Button>
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            render: (text, record) => <Button type="link" onClick={() => selectJob(record)} href={"job?id=" + record.id} size="small">
                <span className={classes.nameCell}>{text}</span> </Button>
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            filters: [{ text: 'Celery', value: 'celery' }, { text: 'Scrapy', value: 'scrapy' },],
            onFilter: (value, record) => record.type.indexOf(value) === 0
        },
        {
            title: 'User',
            dataIndex: 'created_by_name',
            key: 'created_by_name',
            filters: usersFilter,
            onFilter: (value, record) => record.created_by_name.indexOf(value) === 0
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: [
                { text: 'PENDING', value: 'PENDING' },
                { text: 'PROCESSING', value: 'PROCESSING' },
                { text: 'PAUSED', value: 'PAUSED' },
                { text: 'TERMINATED', value: 'TERMINATED' },
                { text: 'DONE', value: 'DONE' },
                { text: 'ERROR', value: 'ERROR' },
                { text: 'RETRIED', value: 'RETRIED' },
            ],
            sorter: (a, b) => a.status.length - b.status.length,
            render: (text, _) => <JobStatus status={text} />,
            onFilter: (value, record) => record.status.indexOf(value) === 0,
        },
        {
            key: 'tool',
            title: 'Tool Name',
            dataIndex: 'tool_name',
            filters: allTools,
            onFilter: (value, record) => record.tool_name.indexOf(value) === 0,
            sorter: (a, b) => a.tool_name.toLowerCase().localeCompare(b.tool_name.toLowerCase()),
            render: (text, record) => <Button type="link" onClick={() => onSelectTool(record.tool)} size="small">
                <span className={classes.nameCell}> {text}</span> </Button>
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a, b) => (new Date(a.created_at) > new Date(b.created_at)) ? 1 : 0,
            render: created_at => (
                moment(created_at).format("YYYY-MM-DD HH:mm"))
        },
        {
            title: 'Last Updated Date',
            dataIndex: 'updated_at',
            key: 'updated_at',
            sorter: (a, b) => (new Date(a.updated_at) > new Date(b.updated_at)) ? 1 : 0,
            render: updated_at => (
                moment(updated_at).format("YYYY-MM-DD HH:mm"))
        },
        {
            title: '#',
            key: 'actions',
            render: (_, record) => {
                return (
                    <Space>
                        <Popconfirm
                            title="Are you sure to delete this job?"
                            onConfirm={() => deleteJob(record)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button danger size='small' icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ]

    const changePage = (page, _) => {
        updatePage(page);
    }

    const changeSize = (page, size) => {
        updateLimit(size)
    }

    const handleTableChange = (pagination, filters, sorter) => {
        const field = sorter.field;
        const order = sorter.order;

        if (field && order) {
            const ordering = order == "descend" ? `-${field}` : field;
            setOrdering(ordering);
        }
        if (filters.created_by_name) {
            setUserName(filters.created_by_name.join(","));
        }
        else {
            setUserName("")
        }
        if (filters.id) {
            setToolName(filters.tool.join(","));
        }
        else {
            setToolName("")
        }
        if (filters.tool) {
            setToolName(filters.tool.join(","));
        }
        else {
            setToolName("")
        }
        if (filters.status) {
            setFilterBy(filters.status.join(","))
        } else {
            setFilterBy([])
        }
        if (filters.type) {
            setTypeBy(filters.type.join(","))
        } else {
            setTypeBy([])
        }
        if (filters.tool) {
            const selectedToolKeys = filters.tool.map(name => {
                const toolKey = allTools.findIndex(tool => tool.text === name);
                return toolKey !== -1 ? toolKey + 1 : null;
            }).filter(key => key !== null);  // Remove null values
        
            setToolId(selectedToolKeys);
            
        }else{
            setToolId([])
        }
    }


    return <Table
        columns={jobsViewSchema}
        size='small'
        dataSource={jobs}
        className={classes.table}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
            onChange: changePage,
            onShowSizeChange: changeSize,
            showSizeChanger: true,
            total: count,
            current: page,
            pageSize: limit,
        }}
    />
}

JobsTableView.propTypes = {
    /** List of fetched jobs */
    jobs: PropTypes.array,
    /** Callback for job selection */
    selectJob: PropTypes.func,
    /** Callback for job selection */
    stopJob: PropTypes.func,
    /** Control table loader */
    loading: PropTypes.bool
}

export default JobsTableView
